/**
 * Perfis e contexto de navegacao.
 *
 * Espelha db/006_usuarios.sql. A regra de negocio esta no banco (CHECK
 * `papeis_coerentes`), nao aqui: isto e so o vocabulario do cliente.
 *
 *   tipo = 'cliente'   assina planos e subscreve portfolios
 *   tipo = 'provedor'  e_estrategista e/ou e_gestor (pode ser os dois)
 *
 * Um provedor NAO pode ser cliente -- travado nesta fase, e garantido
 * pelo banco.
 *
 * Tudo aqui e para DECIDIR O QUE MOSTRAR. Nunca para autorizar: a rota e
 * UX, e quem barra acesso e a API, em todo request. Um cliente que digita
 * /admin na barra tem que receber 403 do servidor, nao so uma tela vazia.
 */

export type TipoUsuario = 'cliente' | 'provedor';

/** Os tres modulos do produto. */
export type Modulo = 'admin' | 'marketplace' | 'management';

/**
 * Um contexto e "em nome de que o usuario esta navegando agora".
 *
 * Um provedor que e estrategista E gestor tem dois contextos; um cliente
 * que assina dois gestores tambem. O `gestorId` importa no Management: e
 * ele que escolhe a marca a aplicar.
 */
export interface Contexto {
  modulo: Modulo;
  rotulo: string;
  gestorId?: string;
}

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  tipo: TipoUsuario;
  eEstrategista: boolean;
  eGestor: boolean;
  /** Operador da plataforma. Ortogonal ao tipo: nao vem de `users.tipo`. */
  eAdmin: boolean;
  /** Estado na esteira de cadastro. Ver db/009_provedores.sql. */
  status: string;
  contextos: Contexto[];
}

export const INICIO_DO_MODULO: Record<Modulo, string> = {
  admin: '/admin/frota',
  marketplace: '/marketplace',
  management: '/management',
};

/** Para onde mandar o usuario ao entrar, sem que ele escolha. */
export function contextoPadrao(u: Usuario): Contexto | null {
  if (u.contextos.length === 0) return null;
  // Admin primeiro: quem opera a plataforma quer o backoffice.
  return u.contextos.find((c) => c.modulo === 'admin') ?? u.contextos[0];
}

export function podeVerModulo(u: Usuario | null, modulo: Modulo): boolean {
  return !!u?.contextos.some((c) => c.modulo === modulo);
}

export function ehProvedor(u: Usuario | null): boolean {
  return u?.tipo === 'provedor';
}

export function ehCliente(u: Usuario | null): boolean {
  return u?.tipo === 'cliente';
}

/**
 * Provedor que ainda tem o formulario de cadastro POR PREENCHER: o
 * incompleto, o recusado (corrige e reenvia) e o suspenso.
 *
 * Ele TEM login -- precisa, para se cadastrar -- e /cadastro e o unico
 * lugar onde ha algo a fazer. Cair na gestao vazia nao diria a ele qual
 * e o proximo passo.
 *
 * EM ANALISE NAO ENTRA AQUI (09/10/2026): ate entao, quem ja tinha
 * enviado o cadastro ficava preso numa tela de "aguarde", sem nada para
 * fazer alem de sair. Agora vai para a home, que avisa da analise numa
 * faixa, e pode preparar estrategias e contas enquanto espera -- a API ja
 * permitia (exige_provedor). Publicar depende das aprovacoes do admin.
 *
 * Admin fica de fora: quem opera a plataforma nao passa por esta esteira.
 */
export function precisaCompletarCadastro(u: Usuario | null): boolean {
  if (!u || u.tipo !== 'provedor' || u.eAdmin) return false;
  return u.status !== 'ativo' && u.status !== 'em_analise';
}

/** Provedor que ja enviou o cadastro e espera a decisao do admin. */
export function cadastroEmAnalise(u: Usuario | null): boolean {
  return !!u && u.tipo === 'provedor' && !u.eAdmin && u.status === 'em_analise';
}
